import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { CreateTestRequst, fetchUserDataRequest } from '../../actions/dashboardActions';
import { FaDownload, FaInfoCircle, FaExclamationTriangle, FaCheckCircle, FaTimes, FaFileExcel, FaSpinner, FaUpload } from 'react-icons/fa';
import './ExcelExamUpload.css';

const ExcelExamUpload = () => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const teacherProfileData = useSelector(state => state.dashboard.userData);

  useEffect(() => {
    if (!teacherProfileData) {
      dispatch(fetchUserDataRequest());
    }
  }, [dispatch, teacherProfileData]);

  // Download the existing template
  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/Exam_Creation_Template.xlsx';
    link.download = 'Exam_Creation_Template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    // Reset states
    setUploadProgress(0);
    setValidationErrors([]);
    setSuccessMessage('');

    // Check if file is Excel format
    if (!uploadedFile.name.match(/\.(xlsx|xls)$/)) {
      setValidationErrors(["Please upload a valid Excel file (.xlsx or .xls)"]);
      return;
    }

    setFile(uploadedFile);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  // Process the uploaded file
  const processFile = () => {
    if (!file) {
      setValidationErrors(["Please select a file first"]);
      return;
    }

    setIsProcessing(true);
    setValidationErrors([]);
    setSuccessMessage('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Check if ExamData sheet exists
        if (!workbook.SheetNames.includes("ExamData")) {
          setValidationErrors(["The uploaded file is missing the 'ExamData' sheet"]);
          setIsProcessing(false);
          return;
        }

        // Parse the ExamData sheet
        const worksheet = workbook.Sheets["ExamData"];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        // Validate and process the data
        const processedData = validateAndProcessData(jsonData);

        if (processedData.errors.length > 0) {
          setValidationErrors(processedData.errors);
          setIsProcessing(false);
          return;
        }

        // Dispatch the action to create the test
        dispatch(CreateTestRequst(processedData.testData));

        // Reset the file input
        setFile(null);
        setUploadProgress(0);
        document.getElementById('file-upload').value = '';

        // Show success message
        setSuccessMessage("Exam created successfully!");

      } catch (error) {
        setValidationErrors(["Error processing the file: " + error.message]);
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setValidationErrors(["Error reading the file"]);
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // Validate and process the Excel data
  const validateAndProcessData = (jsonData) => {
    const errors = [];
    const testData = {
      teacherId: teacherProfileData?._id,
      teacherName: teacherProfileData?.name,
      questions: [],
      correctAnswers: []
    };

    // Find the start of TEST DETAILS and QUESTIONS DATA
    let testDetailsStart = -1;
    let questionsStart = -1;

    for (let i = 0; i < jsonData.length; i++) {
      if (jsonData[i][0] === "TEST DETAILS") {
        testDetailsStart = i + 2;
      }
      if (jsonData[i][0] === "QUESTIONS DATA") {
        questionsStart = i + 2;
        break;
      }
    }

    if (testDetailsStart === -1) {
      errors.push("Could not find TEST DETAILS section");
      return { errors, testData: null };
    }

    if (questionsStart === -1) {
      errors.push("Could not find QUESTIONS DATA section");
      return { errors, testData: null };
    }

    // Process test details
    for (let i = testDetailsStart; i < jsonData.length; i++) {
      if (!jsonData[i] || jsonData[i].length === 0 || !jsonData[i][0]) break;

      const field = jsonData[i][0];
      const value = jsonData[i][1];

      switch (field) {
        case "Test Name":
          if (!value) errors.push("Test Name is required");
          testData.testName = value;
          break;
        case "Category":
          if (!value) errors.push("Category is required");
          testData.category = value;
          break;
        case "Duration (minutes)":
          if (!value || isNaN(value) || value <= 0) errors.push("Duration must be a positive number");
          testData.totalMinutes = parseInt(value);
          break;
        case "Total Marks":
          if (!value || isNaN(value) || value <= 0) errors.push("Total Marks must be a positive number");
          testData.totalMarks = parseInt(value);
          break;
        case "Test Series":
          testData.testSeries = value || "NA";
          break;
        case "Passing Percentage":
          if (!value || isNaN(value) || value < 0 || value > 100) {
            errors.push("Passing Percentage must be between 0 and 100");
          }
          testData.passingPercentage = parseFloat(value);
          break;
        case "Default Question Type":
          if (value && !["single", "multiple"].includes(value.toLowerCase())) {
            errors.push("Default Question Type must be either 'single' or 'multiple'");
          }
          break;
        case "Mark Distribution":
          if (!value || !["total", "question level"].includes(value.toLowerCase())) {
            errors.push("Mark Distribution must be either 'total' or 'question level'");
          }
          testData.markDistribution = value.toLowerCase();
          break;
        case "Custom Category":
          if (testData.category === "other" && !value) {
            errors.push("Custom Category is required when Category is 'other'");
          }
          break;
        default:
          // Ignore unknown fields
          break;
      }
    }

    // Process questions
    for (let i = questionsStart; i < jsonData.length; i++) {
      if (!jsonData[i] || jsonData[i].length === 0 || !jsonData[i][0]) continue;

      const question = jsonData[i][0];
      const optionA = jsonData[i][1];
      const optionB = jsonData[i][2];
      const optionC = jsonData[i][3];
      const optionD = jsonData[i][4];
      const correctAnswer = jsonData[i][5];
      const type = jsonData[i][6];
      const explanation = jsonData[i][7];
      const marks = jsonData[i][8];

      // Validate required fields
      if (!question) {
        errors.push(`Row ${i + 1}: Question text is required`);
        continue;
      }

      if (!optionA || !optionB || !optionC || !optionD) {
        errors.push(`Row ${i + 1}: All four options are required`);
        continue;
      }

      if (!correctAnswer) {
        errors.push(`Row ${i + 1}: Correct answer is required`);
        continue;
      }

      // Validate question type
      const questionType = (type || "single").toLowerCase();
      if (!["single", "multiple"].includes(questionType)) {
        errors.push(`Row ${i + 1}: Type must be either 'single' or 'multiple'`);
        continue;
      }

      // Validate correct answer format (now accepts A, B, C, D)
      let correctAnswers;
      if (questionType === "single") {
        if (correctAnswer.includes(",")) {
          errors.push(`Row ${i + 1}: Single choice questions should have only one correct answer`);
          continue;
        }

        // Convert to uppercase and trim any whitespace
        const answerLetter = correctAnswer.toString().toUpperCase().trim();

        // Validate it's a valid option letter
        if (!["OPTION A", "OPTION B", "OPTION C", "OPTION D"].includes(answerLetter)) {
          errors.push(`Row ${i + 1}: Correct answer must be Option A, Option B, Option C, or Option D for single choice questions`);
          continue;
        }

        // Convert letter to index (A=0, B=1, C=2, D=3)
        correctAnswers = ["OPTION A", "OPTION B", "OPTION C", "OPTION D"].indexOf(answerLetter);
      } else {
        // Multiple choice - split by comma and process each answer
        const answerLetters = correctAnswer.split(",").map(letter => letter.toUpperCase().trim());

        // Validate each answer is a valid option letter
        if (answerLetters.some(letter => !["OPTION A", "OPTION B", "OPTION C", "OPTION D"].includes(letter))) {
          errors.push(`Row ${i + 1}: Correct answers must be Option A, Option B, Option C, or Option D separated by commas (e.g., "Option A, Option C")`);
          continue;
        }

        // Check for duplicate answers
        const uniqueAnswers = [...new Set(answerLetters)];
        if (uniqueAnswers.length !== answerLetters.length) {
          errors.push(`Row ${i + 1}: Duplicate answers are not allowed`);
          continue;
        }

        // Convert letters to indices (A=0, B=1, C=2, D=3)
        correctAnswers = answerLetters.map(letter => ["OPTION A", "OPTION B", "OPTION C", "OPTION D"].indexOf(letter));
      }

      // Validate marks if mark distribution is by question
      if (testData.markDistribution === "question level" && (!marks || isNaN(marks) || marks <= 0)) {
        errors.push(`Row ${i + 1}: Marks must be a positive number when mark distribution is by question level`);
        continue;
      }

      // Create question object
      const questionObj = {
        questionNumber: i - questionsStart + 1,
        questionText: question,
        options: [optionA, optionB, optionC, optionD],
        type: questionType,
        explanation: explanation || null,
        marks: testData.markDistribution === "question level" ? parseFloat(marks) : null
      };

      testData.questions.push(questionObj);
      testData.correctAnswers.push(correctAnswers);
    }

    // Validate at least one question
    if (testData.questions.length === 0) {
      errors.push("At least one question is required");
    }

    return { errors, testData: errors.length === 0 ? testData : null };
  };

  // Clear file selection
  const clearFile = () => {
    setFile(null);
    setUploadProgress(0);
    setValidationErrors([]);
    setSuccessMessage('');
    document.getElementById('file-upload').value = '';
  };

  return (
    <div className="excel-upload-container">
      <h2 className="excel-upload-title">
        <FaFileExcel className="title-icon" />
        Create Exam from Excel
      </h2>

      <div className="instructions-box">
        <h3 className="instructions-title">
          <FaInfoCircle className="icon" />
          Instructions
        </h3>
        <ol className="instructions-list">
          <li>Download the Excel template using the button below</li>
          <li>Fill in your exam details in the ExamData sheet</li>
          <li>Do not modify the column headers</li>
          <li>For correct answers, use option letters (A, B, C, D)</li>
          <li>For multiple correct answers, separate option letters with commas (e.g., "A,C")</li>
          <li>Upload the completed file using the form below</li>
        </ol>
      </div>

      <div className="template-section">
        <button
          onClick={downloadTemplate}
          className="download-btn"
          disabled={isProcessing}
        >
          <FaDownload className="btn-icon" />
          Download Template
        </button>

        <div className="file-types">
          Supported file types: .xlsx, .xls
        </div>
      </div>

      <div className="upload-area">
        <label htmlFor="file-upload" className="upload-label">
          <div className="upload-content">
            <FaUpload className="upload-icon" />
            <p className="upload-text">
              <span className="highlight">Click to upload</span> or drag and drop
            </p>
            <p className="upload-subtext">Maximum file size: 10MB</p>
          </div>
        </label>
        <input
          id="file-upload"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          className="file-input"
          disabled={isProcessing}
        />

        {file && (
          <div className="file-preview">
            <div className="file-info">
              <FaFileExcel className="file-icon" />
              <div className="file-details">
                <span className="file-name">{file.name}</span>
                <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
              <button
                onClick={clearFile}
                className="clear-btn"
                disabled={isProcessing}
              >
                <FaTimes />
              </button>
            </div>

            {uploadProgress > 0 && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="progress-text">
                  {uploadProgress}% uploaded
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {validationErrors.length > 0 && (
        <div className="error-box">
          <h3 className="error-title">
            <FaExclamationTriangle className="icon" />
            Validation Errors
          </h3>
          <ul className="error-list">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {successMessage && (
        <div className="success-box">
          <FaCheckCircle className="success-icon" />
          <span className="success-text">{successMessage}</span>
        </div>
      )}

      <div className="action-section">
        <button
          onClick={processFile}
          disabled={isProcessing || !file}
          className={`process-btn ${isProcessing || !file ? 'disabled' : ''}`}
        >
          {isProcessing ? (
            <>
              <FaSpinner className="spinner-icon" />
              Processing...
            </>
          ) : (
            <>
              <FaCheckCircle className="btn-icon" />
              Create Exam
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ExcelExamUpload;